import { useState } from 'react';
import { Stack, Tooltip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { TableRow, TableCell, Typography, Button } from '@occc/ui-design-system';
import { parseDate, formatDateTime, DateTimeFormat } from '@occc-app/lib/date';

import { ExternalLoyaltyManualOffer } from '../../../tobacco-api-offers.types';
import {
  STATUS_COLOR,
  STATUS_LABEL,
  ACTIVATE_STATUS_COLOR,
  ACTIVATE_STATUS_LABEL,
} from '../../../tobacco-api-offers.constants';
import { CreateOfferDialog } from './create-offer-dialog.component';
import { formatStates, getRowAction, TABLE_VISIBLE_STATES_COUNT } from '../tobacco-api-offers.utils';

const TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'text.primary',
      opacity: 0.9,
      borderRadius: 2,
      p: 1.5,
      fontSize: '1.4rem',
    },
  },
  arrow: { sx: { color: 'text.primary' } },
} as const;

type OffersTableRowProps = {
  offer: ExternalLoyaltyManualOffer;
  onClick: (uuid: string) => void;
  onCreateAction?: (uuid: string, offerName: string) => void;
  onDeleteAction?: (uuid: string) => void;
  actionLoadingUuid?: string;
};

export const OffersTableRow = ({ offer, onClick, onCreateAction, onDeleteAction, actionLoadingUuid }: OffersTableRowProps) => {
  const startDate = formatDateTime(parseDate(offer.startDate), DateTimeFormat.DateSlashed);
  const endDate = formatDateTime(parseDate(offer.endDate), DateTimeFormat.DateSlashed);
  const runDates = `${startDate} - ${endDate}`;
  const action = getRowAction(offer);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  return (
    <TableRow
      hover
      onClick={() => onClick(offer.uuid)}
      sx={{
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        ...(offer.hasError && {
          backgroundColor: 'error.light',
          '&:hover': {
            backgroundColor: 'error.light',
          },
        }),
      }}
    >
      <TableCell>
        <Stack direction='row' alignItems='center' spacing={1}>
          {offer.hasError && (
            <Tooltip title={offer.errorMessage ?? 'Unknown error'} arrow placement='top' slotProps={TOOLTIP_SLOT_PROPS}>
              <ErrorOutlineIcon color='error' fontSize='small' sx={{ flexShrink: 0 }} />
            </Tooltip>
          )}
          {offer.name.length > 20 ? (
            <Tooltip title={offer.name} arrow placement='top' slotProps={TOOLTIP_SLOT_PROPS}>
              <Typography variant='body2'>{`${offer.name.slice(0, 20)}…`}</Typography>
            </Tooltip>
          ) : (
            <Typography variant='body2'>{offer.name}</Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell>{offer.externalCampaignId}</TableCell>
      <TableCell>{offer.brand}</TableCell>
      <TableCell>{offer.manufacturer}</TableCell>
      <TableCell>
        {offer.states.length > TABLE_VISIBLE_STATES_COUNT ? (
          <Tooltip title={offer.states.join(', ')} arrow placement='top' slotProps={TOOLTIP_SLOT_PROPS}>
            <Typography variant='body2' sx={{ width: 'fit-content' }}>
              {formatStates(offer.states, TABLE_VISIBLE_STATES_COUNT)}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant='body2'>{formatStates(offer.states, TABLE_VISIBLE_STATES_COUNT)}</Typography>
        )}
      </TableCell>
      <TableCell>{offer.storeCount}</TableCell>
      <TableCell>{runDates}</TableCell>
      <TableCell>
        <Typography variant='body2' sx={{ color: STATUS_COLOR[offer.status] }}>
          {STATUS_LABEL[offer.status]}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2' sx={{ color: ACTIVATE_STATUS_COLOR[offer.activateStatus] }}>
          {ACTIVATE_STATUS_LABEL[offer.activateStatus]}
        </Typography>
      </TableCell>
      <TableCell>
        {action && (onCreateAction || onDeleteAction || action.disabled) && (
          <Button
            variant='contained'
            color={action.color}
            size='small'
            disabled={action.disabled || actionLoadingUuid === offer.uuid}
            sx={{ minWidth: 100 }}
            onClick={(e) => {
              e.stopPropagation();
              if (action.actionType === 'create') {
                setIsCreateDialogOpen(true);
              } else if (action.actionType === 'delete') {
                onDeleteAction?.(offer.uuid);
              }
            }}
          >
            {action.label}
          </Button>
        )}
        {isCreateDialogOpen && (
          <CreateOfferDialog
            initialName={offer.name}
            onClose={() => setIsCreateDialogOpen(false)}
            onSubmit={(offerName) => {
              setIsCreateDialogOpen(false);
              onCreateAction?.(offer.uuid, offerName);
            }}
          />
        )}
      </TableCell>
    </TableRow>
  );
};
